// ═══════════════════════════════════════════════════════════════
// KOTCH - FACTION DECK DEFINITIONS
// Each faction has themed decks with balanced special card counts
// ═══════════════════════════════════════════════════════════════

import type { Faction, FactionAbility } from "../../types/kotch";

// ─────────────────────────────────────────────────────────────────
// FACTION ABILITIES (triggered by winning the center lane)
// ─────────────────────────────────────────────────────────────────

export const FACTION_ABILITIES: Record<Faction, FactionAbility> = {
  Migora: {
    id: "ralms_united",
    name: "Ralms United",
    description: "Return one of your flank cards from this round to your hand",
    effect: "return_flank_card",
  },
  Hydaria: {
    id: "mantis_network",
    name: "Mantis Network",
    description:
      "Discard a card from opponent's hand (specials only if revealed)",
    effect: "discard_opponent_hand",
  },
  Motra: {
    id: "solar_summits_blessing",
    name: "Solar Summit's Blessing",
    description:
      "Your center lane winner stays on the board next round, moving to a flank",
    effect: "persist_center_card",
  },
  Rigata: {
    id: "rigata_green",
    name: "Rigata Green",
    description: "Draw a card from your deck",
    effect: "draw_card",
  },
  Vera: {
    id: "the_five_city_states",
    name: "The Five City States",
    description: "Draw a card from your deck",
    effect: "draw_card",
  },
};

// ─────────────────────────────────────────────────────────────────
// FACTION METADATA
// Theme colors, names, and descriptions
// ─────────────────────────────────────────────────────────────────

export interface FactionInfo {
  id: Faction;
  name: string;
  fullName: string;
  description: string;
  themeColor: string;
  theme: string;
}

export const FACTION_INFO: Record<Faction, FactionInfo> = {
  Migora: {
    id: "Migora",
    name: "Migora",
    fullName: "The Ralmera of Migora",
    description: "Hardy warriors known for their unyielding strength",
    themeColor: "#d4af37",
    theme: "Strength & Endurance",
  },
  Hydaria: {
    id: "Hydaria",
    name: "Hydaria",
    fullName: "The Empire of Hydaria",
    description: "Masters of information and subterfuge",
    themeColor: "#c62828",
    theme: "Information & Control",
  },
  Motra: {
    id: "Motra",
    name: "Motra",
    fullName: "The Stone Sovereignty of Motra",
    description: "Ancient power built on sacrifice and stone",
    themeColor: "#64b5f6",
    theme: "Power & Sacrifice",
  },
  Rigata: {
    id: "Rigata",
    name: "Rigata",
    fullName: "The Evergreen Realm of Rigata",
    description: "Noble bloodlines with powerful synergies",
    themeColor: "#228b22",
    theme: "Synergy & Royalty",
  },
  Vera: {
    id: "Vera",
    name: "Vera",
    fullName: "The Free City-States of Vera",
    description: "Led by Polemarchs, Magisters, and Archons in council",
    themeColor: "#9c27b0",
    theme: "Politics & Strategy",
  },
};

// ─────────────────────────────────────────────────────────────────
// FACTION DECK CONFIGURATIONS
// Each deck: 10 special cards (knights + spies) + 37 basic cards = 47 total
// ─────────────────────────────────────────────────────────────────

export interface FactionDeckConfig {
  faction: Faction;
  knightIds: string[]; // IDs of knights belonging to this faction
  spyCount: number; // Number of spies to include (10 - knight count)
  basicDistribution: {
    spearman: number;
    archer: number;
    rider: number;
  };
}

// Knight ID mapping based on KNIGHTS array order in cards.ts:
// Migora: knight_1 to knight_5 (Mithra, Reyaz, Berjot, Hafler, Archralm Alon)
// Hydaria: knight_6 to knight_8 (Armsmaster Lysiac, Battlemaster Lysiac, Ragos)
// Motra: knight_9 to knight_10 (Stone Sovereign Farkion, Councilor Farkion)
// Rigata: knight_11 to knight_15 (Arog, Renedil, Averil, Quietus, Gimora)
// Vera: knight_16 to knight_20 (Shadowlord Veris, Silent Blade Kira, Truthseeker Nira, Nightwalker Seren, Mistweaver Thane)
// Eleia: knight_21 (Migora)

export const FACTION_DECK_CONFIGS: Record<Faction, FactionDeckConfig> = {
  Migora: {
    faction: "Migora",
    knightIds: [
      "knight_1",
      "knight_2",
      "knight_3",
      "knight_4",
      "knight_5",
      "knight_21",
    ],
    spyCount: 4, // 10 - 6 knights = 4 spies
    basicDistribution: { spearman: 13, archer: 12, rider: 12 }, // 37 total
  },
  Hydaria: {
    faction: "Hydaria",
    knightIds: ["knight_6", "knight_7", "knight_8"],
    spyCount: 7, // 10 - 3 knights = 7 spies (more spies fits their subterfuge theme)
    basicDistribution: { spearman: 12, archer: 13, rider: 12 },
  },
  Motra: {
    faction: "Motra",
    knightIds: ["knight_9", "knight_10"],
    spyCount: 8, // 10 - 2 knights = 8 spies
    basicDistribution: { spearman: 13, archer: 12, rider: 12 },
  },
  Rigata: {
    faction: "Rigata",
    knightIds: [
      "knight_11",
      "knight_12",
      "knight_13",
      "knight_14",
      "knight_15",
    ],
    spyCount: 5, // 10 - 5 knights = 5 spies
    basicDistribution: { spearman: 12, archer: 12, rider: 13 },
  },
  Vera: {
    faction: "Vera",
    knightIds: [
      "knight_16",
      "knight_17",
      "knight_18",
      "knight_19",
      "knight_20",
    ],
    spyCount: 5, // 10 - 5 knights = 5 spies
    basicDistribution: { spearman: 12, archer: 13, rider: 12 },
  },
};

// ─────────────────────────────────────────────────────────────────
// HELPER FUNCTIONS
// ─────────────────────────────────────────────────────────────────

export function getFactionInfo(faction: Faction): FactionInfo {
  return FACTION_INFO[faction];
}

export function getFactionAbility(faction: Faction): FactionAbility {
  return FACTION_ABILITIES[faction];
}

export function getFactionDeckConfig(faction: Faction): FactionDeckConfig {
  return FACTION_DECK_CONFIGS[faction];
}

export function getAllFactions(): Faction[] {
  return ["Migora", "Hydaria", "Motra", "Rigata", "Vera"];
}

export function getRandomFaction(): Faction {
  const factions = getAllFactions();
  return factions[Math.floor(Math.random() * factions.length)];
}
